import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";

const getGeolocation = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser.");
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        console.error("Error acquiring geolocation:", error);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
};

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const resolveEngineerNotificationRoute = (notification) => {
  if (!notification) return "/engineer-dashboard";
  if (notification.type === "complaint-assigned" && notification.relatedId) {
    return "/engineer-complaints";
  }
  if ((notification.type === "report-approved" || notification.type === "report-submitted") && notification.relatedId) {
    return `/my-reports/${notification.relatedId}`;
  }
  if (notification.type === "report-changes-requested" && notification.relatedId) {
    return `/create-report/${notification.relatedId}`;
  }
  return "/engineer-dashboard";
};

const formatAddress = (address) => {
  if (!address) return "-";
  if (typeof address === "string") return address;

  return [address.street, address.area, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");
};

const formatDateTime = (value) => {
  if (!value) return "Recent";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Recent";

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getNextTravelAction = (travel) => {
  const status = travel?.status || "Not Started";

  if (status === "Not Started") {
    return { label: "Start Journey", status: "Started", timeField: "startTime", confirm: "Start journey for this complaint?" };
  }
  if (status === "Started") {
    return { label: "At Site", status: "At Site", timeField: "reachSiteTime", confirm: "Confirm that you reached the site?" };
  }
  if (status === "At Site") {
    return { label: "Left Site", status: "Left Site", timeField: "leaveSiteTime", confirm: "Confirm that you left the site?" };
  }
  if (status === "Left Site") {
    return { label: "Reached Home", status: "Completed", timeField: "reachHomeTime", confirm: "Confirm that you reached home?" };
  }

  return null;
};

const canCreateReportFromTravel = (travel) => {
  const status = String(travel?.status || "").trim().toLowerCase();
  return status === "at site" || status === "left site" || status === "completed";
};

const isNewerReport = (candidate, current) =>
  new Date(candidate?.updatedAt || candidate?.createdAt || 0) >
  new Date(current?.updatedAt || current?.createdAt || 0);

const getNotificationWeight = (notification) => {
  if (notification?.type === "complaint-assigned" && !notification?.isRead) return 0;
  if (notification?.type === "complaint-assigned") return 1;
  if (!notification?.isRead) return 2;
  return 3;
};

const getNotificationTheme = (notification) => {
  if (notification?.type === "complaint-assigned") {
    return {
      border: "border-rose-200",
      bg: "bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.18),_transparent_38%),linear-gradient(135deg,_rgba(255,241,242,1),_rgba(255,255,255,1),_rgba(254,205,211,0.92))]",
      badge: "bg-rose-600 text-white",
      accent: "text-rose-700",
      label: "High Priority",
      stripe: "bg-rose-500"
    };
  }

  if (notification?.type === "report-approved") {
    return {
      border: "border-emerald-200",
      bg: "bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_38%),linear-gradient(135deg,_rgba(236,253,245,1),_rgba(255,255,255,1),_rgba(209,250,229,0.9))]",
      badge: "bg-emerald-600 text-white",
      accent: "text-emerald-700",
      label: "Report",
      stripe: "bg-emerald-500"
    };
  }

  return {
    border: "border-blue-200",
    bg: "bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.18),_transparent_38%),linear-gradient(135deg,_rgba(239,246,255,1),_rgba(255,255,255,1),_rgba(224,242,254,0.9))]",
    badge: "bg-blue-600 text-white",
    accent: "text-blue-700",
    label: "Update",
    stripe: "bg-blue-500"
  };
};

const buildDashboardCards = (summary) => ([
  {
    title: "Total Complaints",
    value: summary.total,
    helper: "All complaints assigned to you",
    accent: "text-sky-700",
    bg: "from-sky-50 to-blue-100"
  },
  {
    title: "Pending Acceptance",
    value: summary.pendingAcceptance,
    helper: "Needs your acceptance",
    accent: "text-amber-700",
    bg: "from-amber-50 to-yellow-100"
  },
  {
    title: "Reports Pending",
    value: summary.reportPending,
    helper: "Create or finish report work",
    accent: "text-violet-700",
    bg: "from-violet-50 to-fuchsia-100"
  },
  {
    title: "Travel Active",
    value: summary.travelActive,
    helper: "Journey not completed yet",
    accent: "text-emerald-700",
    bg: "from-emerald-50 to-green-100"
  }
]);

function EngineerDashboard() {
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [reportByComplaintId, setReportByComplaintId] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [travelByComplaintId, setTravelByComplaintId] = useState({});
  const [updatingTravelId, setUpdatingTravelId] = useState("");
  const [acceptingComplaintId, setAcceptingComplaintId] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  const engineerName = localStorage.getItem("engineerName");
  const engineerEmail = localStorage.getItem("engineerEmail");
  const engineerId = localStorage.getItem("engineerId");
  const engineerLookup =
    engineerId ||
    engineerEmail ||
    engineerName;
  const engineerAliases = [engineerId, engineerEmail, engineerName]
    .filter(Boolean)
    .join(",");

  const engineerMatches = useMemo(
    () =>
      [engineerName, engineerEmail, engineerId, engineerLookup]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [engineerEmail, engineerId, engineerLookup, engineerName]
  );

  const loadDashboard = useCallback(async () => {
    if (!engineerLookup) {
      setComplaints([]);
      setReportByComplaintId({});
      setTravelByComplaintId({});
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [complaintsRes, reportsRes, travelRes, notificationsRes] = await Promise.all([
        axios.get(API_BASE_URL + `/api/complaints/engineer/${encodeURIComponent(engineerLookup)}`),
        axios.get(API_BASE_URL + "/api/visits", {
          params: { engineer: engineerLookup, excludePdf: 1 }
        }),
        axios.get(API_BASE_URL + "/api/travel"),
        axios.get(API_BASE_URL + "/api/notifications", {
          params: { role: "engineer", user: engineerAliases }
        })
      ]);

      const complaintData = Array.isArray(complaintsRes.data) ? complaintsRes.data : [];
      const acceptedComplaintIds = new Set(
        complaintData
          .filter((complaint) => complaint.assignmentStatus === "accepted")
          .flatMap((complaint) => [String(complaint._id || ""), String(complaint.complaintId || "")])
          .filter(Boolean)
      );
      const reports = Array.isArray(reportsRes.data) ? reportsRes.data : [];
      const travels = (Array.isArray(travelRes.data) ? travelRes.data : []).filter((travel) =>
        engineerMatches.includes(String(travel.engineerId || "").trim().toLowerCase()) ||
        engineerMatches.includes(String(travel.engineerName || "").trim().toLowerCase())
      );
      const notificationData = Array.isArray(notificationsRes.data) ? notificationsRes.data : [];

      const reportMap = reports.reduce((acc, report) => {
        [report.complaintId, report.complaintRefId]
          .filter(Boolean)
          .forEach((key) => {
            const normalizedKey = String(key);
            if (acceptedComplaintIds.has(normalizedKey) && (!acc[normalizedKey] || isNewerReport(report, acc[normalizedKey]))) {
              acc[normalizedKey] = report;
            }
          });
        return acc;
      }, {});

      const travelMap = travels.reduce((acc, travel) => {
        const keys = [travel.complaintId, travel.complaintRefId]
          .filter(Boolean)
          .map(String)
          .filter((key) => acceptedComplaintIds.has(key));

        keys.forEach((key) => {
          const existing = acc[key];
          if (!existing || new Date(travel.createdAt) > new Date(existing.createdAt)) {
            acc[key] = travel;
          }
        });
        return acc;
      }, {});

      setComplaints(complaintData);
      setReportByComplaintId(reportMap);
      setTravelByComplaintId(travelMap);
      setNotifications(notificationData);
    } catch (error) {
      console.error(error);
      setComplaints([]);
      setReportByComplaintId({});
      setTravelByComplaintId({});
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [engineerAliases, engineerLookup, engineerMatches]);

  useEffect(() => {
    loadDashboard();

    let intervalId = window.setInterval(loadDashboard, 60000);

    const handleFocus = () => {
      window.clearInterval(intervalId);
      loadDashboard();
      intervalId = window.setInterval(loadDashboard, 60000);
    };

    const handleBlur = () => {
      window.clearInterval(intervalId);
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.clearInterval(intervalId);
    };
  }, [loadDashboard]);

  const openNotification = async (notification) => {
    try {
      if (notification?._id && !notification.isRead) {
        await axios.put(API_BASE_URL + `/api/notifications/${notification._id}/read`);
        setNotifications((current) =>
          current.map((item) =>
            item._id === notification._id ? { ...item, isRead: true } : item
          )
        );
      }
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    } finally {
      navigate(resolveEngineerNotificationRoute(notification));
    }
  };

  const handleAcceptComplaint = async (complaintId) => {
    try {
      setAcceptingComplaintId(complaintId);
      await axios.put(API_BASE_URL + `/api/complaints/${complaintId}/accept`, {
        engineer: engineerLookup
      });
      await loadDashboard();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to accept complaint");
    } finally {
      setAcceptingComplaintId("");
    }
  };

  const handleTravelAction = async (complaint) => {
    if (complaint.assignmentStatus !== "accepted") {
      alert("Accept this complaint before starting travel.");
      return;
    }

    const existingTravel =
      travelByComplaintId[complaint._id] ||
      travelByComplaintId[complaint.complaintId];
    const nextAction = getNextTravelAction(existingTravel);

    if (!nextAction) {
      return;
    }

    const confirmed = window.confirm(nextAction.confirm);
    if (!confirmed) {
      return;
    }

    try {
      setUpdatingTravelId(complaint._id);

      const geo = await getGeolocation();
      const coords = geo ? { latitude: geo.latitude, longitude: geo.longitude } : {};

      let activeTravel = existingTravel;

      if (!activeTravel) {
        let location = "";
        let city = "";
        const validCustomerId =
          complaint.customerId &&
          complaint.customerId !== "ABB-WARRANTY" &&
          /^[a-f\d]{24}$/i.test(String(complaint.customerId));

        if (validCustomerId) {
          try {
            const customerRes = await axios.get(API_BASE_URL + `/api/customers/${complaint.customerId}`);
            const customer = customerRes.data;
            location = formatAddress(customer?.address);
            city = customer?.address?.city || "";
          } catch (e) {
            console.warn("Could not fetch customer details for travel creation", e);
          }
        }

        if (!location || location === "-") {
          location = formatAddress(complaint.address);
          if (!location || location === "-") {
            location = complaint.location || "";
          }
        }

        if (!city) {
          if (complaint.address && typeof complaint.address === "object") {
            city = complaint.address.city || "";
          } else if (complaint.city) {
            city = complaint.city || "";
          }
        }

        const createResponse = await axios.post(API_BASE_URL + "/api/travel", {
          engineerId: engineerLookup,
          engineerName,
          complaintId: complaint._id,
          complaintRefId: complaint.complaintId,
          customerId: complaint.customerId || "",
          company: complaint.company || "",
          location: location === "-" ? "" : location,
          city: city || "",
          ...coords
        });
        activeTravel = createResponse.data;
      }

      const updateResponse = await axios.put(API_BASE_URL + `/api/travel/${activeTravel._id}`, {
        status: nextAction.status,
        timeField: nextAction.timeField,
        ...coords
      });

      const updatedTravel = updateResponse.data;
      setTravelByComplaintId((current) => ({
        ...current,
        [String(complaint._id)]: updatedTravel,
        [String(complaint.complaintId)]: updatedTravel
      }));
    } catch (error) {
      console.error("Failed to update travel status", error);
      alert(error.response?.data?.msg || "Failed to update travel status");
    } finally {
      setUpdatingTravelId("");
    }
  };

  const complaintRows = useMemo(() => {
    return complaints
      .map((complaint) => {
        const existingReport =
          reportByComplaintId[complaint._id] ||
          reportByComplaintId[complaint.complaintId];
        const existingTravel =
          travelByComplaintId[complaint._id] ||
          travelByComplaintId[complaint.complaintId];
        const approvalStatus = normalizeApprovalStatus(existingReport?.approvalStatus);
        const nextTravelAction = getNextTravelAction(existingTravel);

        return {
          ...complaint,
          existingReport,
          existingTravel,
          canCreateReport: canCreateReportFromTravel(existingTravel),
          approvalStatus,
          isAccepted: complaint.assignmentStatus === "accepted",
          isApproved: approvalStatus === "Approved",
          isChangesRequested: approvalStatus === "Changes Requested",
          nextTravelAction,
          updatedAt:
            existingReport?.updatedAt ||
            existingReport?.createdAt ||
            existingTravel?.updatedAt ||
            complaint.updatedAt ||
            complaint.createdAt
        };
      })
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }, [complaints, reportByComplaintId, travelByComplaintId]);

  const summary = useMemo(() => {
    const pendingAcceptance = complaintRows.filter((item) => !item.isAccepted).length;
    const approvedReports = complaintRows.filter((item) => item.isApproved).length;
    const reportPending = complaintRows.filter(
      (item) => item.isAccepted && (!item.existingReport || !item.isApproved)
    ).length;
    const travelActive = complaintRows.filter(
      (item) => item.existingTravel && item.existingTravel.status !== "Completed"
    ).length;

    return {
      total: complaintRows.length,
      pendingAcceptance,
      approvedReports,
      reportPending,
      travelActive
    };
  }, [complaintRows]);

  const dashboardCards = useMemo(() => buildDashboardCards(summary), [summary]);

  const tabs = useMemo(() => ([
    { key: "all", label: "All", count: complaintRows.length },
    { key: "pending", label: "Pending Acceptance", count: summary.pendingAcceptance },
    { key: "reports", label: "Report Work", count: summary.reportPending },
    { key: "travel", label: "Travel Active", count: summary.travelActive },
    { key: "approved", label: "Approved Reports", count: summary.approvedReports }
  ]), [complaintRows.length, summary]);

  const filteredComplaints = useMemo(() => {
    switch (activeTab) {
      case "pending":
        return complaintRows.filter((item) => !item.isAccepted);
      case "reports":
        return complaintRows.filter((item) => item.isAccepted && (!item.existingReport || !item.isApproved));
      case "travel":
        return complaintRows.filter((item) => item.existingTravel && item.existingTravel.status !== "Completed");
      case "approved":
        return complaintRows.filter((item) => item.isApproved);
      case "all":
      default:
        return complaintRows;
    }
  }, [activeTab, complaintRows]);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const weightDifference = getNotificationWeight(a) - getNotificationWeight(b);
      if (weightDifference !== 0) return weightDifference;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [notifications]);

  const visibleNotifications = showAllNotifications
    ? sortedNotifications
    : sortedNotifications.slice(0, 5);

  const highlightComplaints = useMemo(() => filteredComplaints.slice(0, 3), [filteredComplaints]);

  return (
    <EngineerLayout>
      {/* ══ DESKTOP VIEW ══ */}
      <div className="hidden lg:block space-y-5 lg:space-y-8">
        <section className="theme-hero rounded-[24px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.13),_transparent_30%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(224,231,255,0.72))] p-3 shadow-sm sm:p-4 md:p-5 lg:rounded-[32px] lg:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-700 sm:text-xs lg:text-sm lg:tracking-[0.24em]">
                Engineer Workspace
              </p>
              <h1 className="mt-2 text-2xl font-bold text-slate-900 lg:mt-3 lg:text-3xl">Engineer Dashboard</h1>
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3 lg:gap-4">
              {dashboardCards.map((card) => (
                <div
                  key={card.title}
                  className={`theme-stat-card rounded-2xl border border-white/70 bg-gradient-to-br ${card.bg} px-3 py-3 shadow-sm sm:px-3.5 sm:py-3.5 lg:rounded-3xl lg:px-5 lg:py-4`}
                >
                  <p className="theme-stat-label text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500 sm:text-[10px] lg:text-xs lg:tracking-[0.18em]">
                    {card.title}
                  </p>
                  <p className={`mt-2 text-2xl font-bold leading-none ${card.accent} sm:text-3xl lg:mt-3`}>
                    {loading ? "..." : card.value}
                  </p>
                  <p className="theme-stat-helper mt-1.5 text-[10px] text-slate-500 lg:mt-2 lg:text-xs">{card.helper}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 lg:gap-6 xl:grid-cols-[1.35fr_0.95fr]">
          <div className="theme-panel rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:rounded-[30px] lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 lg:text-xl">Complaint Overview</h2>
              </div>

              {loading && (
                <div className="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 lg:px-4 lg:py-2 lg:text-xs lg:tracking-[0.18em]">
                  Refreshing
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2 lg:mt-5 lg:gap-3">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-full px-3 py-2 text-xs font-semibold transition lg:px-4 lg:py-2.5 lg:text-sm ${
                    activeTab === tab.key
                      ? "bg-slate-900 text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {tab.label}: {tab.count}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-3 lg:mt-6 lg:space-y-4">
              {filteredComplaints.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-7 text-center text-sm text-slate-500 lg:rounded-3xl lg:py-10 lg:text-base">
                  No complaints in this section.
                </div>
              ) : (
                highlightComplaints.map((complaint) => (
                  <article
                    key={complaint._id}
                    className="theme-panel rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.72))] p-3.5 lg:rounded-[26px] lg:p-5"
                  >
                    <div className="flex flex-col gap-4 lg:gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2.5 lg:space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-bold text-slate-900 lg:text-lg">{complaint.complaintId}</p>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            complaint.isAccepted
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {complaint.isAccepted ? "Accepted" : "Pending Acceptance"}
                          </span>
                          {complaint.existingReport && (
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              complaint.isApproved
                                ? "bg-sky-100 text-sky-700"
                                : complaint.isChangesRequested
                                ? "bg-rose-100 text-rose-700"
                                : "bg-violet-100 text-violet-700"
                            }`}>
                              {complaint.approvalStatus}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:gap-3">
                          <MiniInfo label="Company" value={complaint.company || "-"} />
                          <MiniInfo label="Product" value={complaint.productType || "-"} />
                          <MiniInfo
                            label="Travel"
                            value={complaint.existingTravel?.status || "Not Started"}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 lg:max-w-[18rem] lg:justify-end">
                        {!complaint.isAccepted ? (
                          <button
                            type="button"
                            onClick={() => handleAcceptComplaint(complaint._id)}
                            disabled={acceptingComplaintId === complaint._id}
                            className="rounded-2xl bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 lg:px-4 lg:py-2.5 lg:text-sm"
                          >
                            {acceptingComplaintId === complaint._id ? "Accepting..." : "Accept"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              complaint.canCreateReport && navigate(
                                complaint.existingReport
                                  ? complaint.isApproved
                                    ? `/my-reports/${complaint.existingReport._id}`
                                    : `/create-report/${complaint.existingReport._id}`
                                  : `/create-report/${complaint._id}`
                              )
                            }
                            className={`rounded-2xl px-3.5 py-2 text-xs font-semibold text-white transition lg:px-4 lg:py-2.5 lg:text-sm ${
                              !complaint.canCreateReport && !complaint.existingReport
                                ? "bg-slate-300 cursor-not-allowed"
                                : complaint.isApproved
                                ? "bg-slate-800 hover:bg-slate-900"
                                : complaint.isChangesRequested
                                ? "bg-rose-600 hover:bg-rose-700"
                                : "bg-blue-600 hover:bg-blue-700"
                            }`}
                            disabled={!complaint.canCreateReport && !complaint.existingReport}
                          >
                            {complaint.existingReport
                              ? complaint.isApproved
                                ? "View Report"
                                : complaint.isChangesRequested
                                ? "Redo Report"
                                : "Edit Report"
                              : "Create Report"}
                          </button>
                        )}

                        {complaint.nextTravelAction ? (
                          <button
                            type="button"
                            onClick={() => handleTravelAction(complaint)}
                            disabled={updatingTravelId === complaint._id}
                            className="rounded-2xl bg-amber-500 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60 lg:px-4 lg:py-2.5 lg:text-sm"
                          >
                            {updatingTravelId === complaint._id ? "Updating..." : complaint.nextTravelAction.label}
                          </button>
                        ) : (
                          <span className="inline-flex rounded-2xl bg-slate-100 px-3.5 py-2 text-xs font-semibold text-slate-500 lg:px-4 lg:py-2.5 lg:text-sm">
                            Travel Completed
                          </span>
                        )}
                      </div>
                      {!complaint.existingReport && complaint.isAccepted && !complaint.canCreateReport && (
                        <p className="text-xs font-medium text-amber-700 lg:text-sm">
                          Report creation unlocks after you mark travel as At Site.
                        </p>
                      )}
                    </div>
                  </article>
                ))
              )}
            </div>

            <div className="mt-4 lg:mt-6">
              <button
                type="button"
                onClick={() => navigate("/engineer-complaints")}
                className="rounded-2xl bg-slate-900 px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-slate-800 lg:px-5 lg:py-3 lg:text-sm"
              >
                View All Complaints
              </button>
            </div>
          </div>

          <section className="theme-panel rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:rounded-[30px] lg:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 lg:text-xl">Notifications</h2>
                <p className="mt-1 text-xs text-slate-500 lg:text-sm">
                  New complaint assignments stay on top with higher priority.
                </p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600 lg:px-4 lg:py-2 lg:text-xs lg:tracking-[0.18em]">
                {sortedNotifications.length} Total
              </div>
            </div>

            {sortedNotifications.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-7 text-center text-sm text-slate-500 lg:mt-6 lg:rounded-3xl lg:py-10 lg:text-base">
                No notifications yet.
              </div>
            ) : (
              <div className="mt-4 space-y-3 lg:mt-6 lg:space-y-4">
                {visibleNotifications.map((notification) => {
                  const theme = getNotificationTheme(notification);

                  return (
                    <button
                      key={notification._id}
                      type="button"
                      onClick={() => openNotification(notification)}
                      className={`theme-notification-card group relative w-full overflow-hidden rounded-2xl border px-3 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg sm:px-4 sm:py-4 lg:rounded-[26px] lg:px-5 lg:py-5 ${theme.border} ${theme.bg} ${
                        notification.isRead ? "opacity-90" : ""
                      }`}
                    >
                      <div className={`absolute left-0 top-0 h-full w-1.5 ${theme.stripe}`} />
                      <div className="flex items-start justify-between gap-3 lg:gap-4">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] sm:text-[10px] lg:px-3 lg:text-[11px] lg:tracking-[0.18em] ${theme.badge}`}>
                              {theme.label}
                            </span>
                            {!notification.isRead && (
                              <span className="rounded-full bg-white/80 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-700 sm:text-[10px] lg:px-3 lg:text-[11px] lg:tracking-[0.18em]">
                                New
                              </span>
                            )}
                          </div>
                          <p className="theme-notification-message mt-2 text-sm font-semibold leading-5 text-slate-900 lg:mt-3 lg:text-base lg:leading-6">
                            {notification.message}
                          </p>
                        </div>

                        <div className="shrink-0 text-right">
                          <p className={`theme-notification-action text-[9px] font-semibold uppercase tracking-[0.12em] sm:text-[10px] lg:text-xs lg:tracking-[0.18em] ${theme.accent}`}>
                            Open
                          </p>
                          <p className="theme-notification-meta mt-1.5 text-[10px] uppercase tracking-wide text-slate-400 lg:mt-2 lg:text-xs">
                            {formatDateTime(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {sortedNotifications.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllNotifications((current) => !current)}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 lg:py-3 lg:text-sm"
                  >
                    {showAllNotifications ? "View Less" : `View More (${sortedNotifications.length - 5} more)`}
                  </button>
                )}
              </div>
            )}
          </section>
        </section>
      </div>

      {/* ══ MOBILE VIEW — premium ══ */}
      <div className="block lg:hidden">
        {/* Heading */}
        <div className="mb-4 mt-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Engineer Workspace</p>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-900">Dashboard</h1>
        </div>

        {/* ── 4 metric cards in a single compact row ── */}
        <div className="mb-5 grid grid-cols-4 gap-2">
          {[
            { label: "Total",    value: summary.total,             to: "/engineer-complaints",              bg: "bg-slate-100",   num: "text-slate-800" },
            { label: "Pending",  value: summary.pendingAcceptance, to: "/engineer-complaints?tab=pending",  bg: "bg-rose-50",     num: "text-rose-700" },
            { label: "Reports",  value: summary.reportPending,     to: "/my-reports",                       bg: "bg-violet-50",   num: "text-violet-700" },
            { label: "Travel",   value: summary.travelActive,      to: "/engineer-complaints?tab=travel",   bg: "bg-amber-50",    num: "text-amber-700" },
          ].map(card => (
            <button key={card.label} type="button" onClick={() => navigate(card.to)}
              className={`mobile-tap-highlight flex flex-col items-center gap-0.5 rounded-2xl ${card.bg} px-1 py-3 text-center shadow-sm active:scale-95 transition-transform`}>
              <span className={`text-[22px] font-black leading-none ${card.num}`}>
                {loading ? <span className="opacity-30 text-base">—</span> : card.value}
              </span>
              <span className="mt-0.5 text-[10px] font-semibold text-slate-500 leading-tight">{card.label}</span>
            </button>
          ))}
        </div>

                {/* ── Quick Actions ── */}
        <div className="mb-5">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Quick Actions</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "My Complaint", to: "/engineer-complaints", color: "bg-slate-100",
                icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></> },
              { label: "Update",    to: "/engineer/daily-update",     color: "bg-blue-100",
                icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
              { label: "Voucher",   to: "/engineer/vouchers",         color: "bg-amber-100",
                icon: <><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></> },
              { label: "My Reports",to: "/my-reports",                color: "bg-violet-100",
                icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></> },
            ].map(({ label, to, color, icon }) => (
              <button key={label} type="button" onClick={() => navigate(to)}
                className="mobile-tap-highlight flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
                <div className={`flex h-14 w-14 items-center justify-center rounded-[18px] ${color} shadow-sm`}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-slate-700">{icon}</svg>
                </div>
                <span className="text-[10px] font-semibold text-slate-600 text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent notifications */}
        {sortedNotifications.length > 0 && (
          <div>
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Recent Activity</p>
            <div className="space-y-2.5">
              {sortedNotifications.slice(0, 3).map(n => {
                const theme = getNotificationTheme(n);
                return (
                  <button key={n._id} type="button" onClick={() => openNotification(n)}
                    className="mobile-tap-highlight flex w-full items-center gap-3.5 rounded-2xl border border-gray-100 bg-white px-4 py-3.5 text-left shadow-sm active:scale-[0.98] transition-transform">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${theme.badge}`}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold text-slate-900">{n.message}</p>
                      <p className="mt-0.5 text-[11px] text-slate-400">{formatDateTime(n.createdAt)}</p>
                    </div>
                    {!n.isRead && <div className="h-2 w-2 shrink-0 rounded-full bg-slate-700" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </EngineerLayout>
  );
}

function MiniInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/85 px-3 py-2.5 ring-1 ring-slate-200 lg:px-4 lg:py-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 lg:text-xs lg:tracking-[0.18em]">{label}</p>
      <p className="mt-1.5 text-xs font-medium text-slate-700 lg:mt-2 lg:text-sm">{value}</p>
    </div>
  );
}

export default EngineerDashboard;


